import { useQuery } from 'react-query';
import {
	getDriveApplicationsForUser,
	getEligibleDrives,
	getOfflineDriveCandidates,
	getOfflineDriveParticipateCandidates,
	getOfflineDrivePositions,
	getOfflineDriveRooms,
} from 'src/apis/offlineDrive';
import { DRIVE_API_REFRESH_INTERVAL } from 'src/config/env.config';
import {
	GetEligibleDrivesResponse,
	GetOfflineDriveCandidatesResponse,
	GetOfflineDrivePositionsResponse,
	UserFiltersForApis,
	GetOfflineDriveRoomsResponse,
	GetDriveApplicationsForUserResponse,
} from 'types';

export const useGetOfflineDriveCandidates = ({
	positionId,
	driveId,
	page,
	limit,
	filters,
	onComplete,
	handleError,
}: {
	driveId: string;
	positionId?: string;
	page: number;
	limit?: number;
	filters?: UserFiltersForApis;
	onComplete?: (data: GetOfflineDriveCandidatesResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getOfflineDriveCandidates', driveId, page, limit, filters, positionId],
		queryFn: async () =>
			(await getOfflineDriveCandidates({
				positionId,
				driveId,
				page,
				limit,
				filters,
			})) as GetOfflineDriveCandidatesResponse,
		staleTime: 0,
		refetchOnMount: true,
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetOfflineDriveParticipateCandidates = ({
	positionId,
	driveId,
	filters,
	onComplete,
	handleError,
}: {
	driveId: string;
	positionId?: string;
	filters?: UserFiltersForApis;
	onComplete?: (data: GetOfflineDriveCandidatesResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getOfflineDriveCandidates', driveId, filters, positionId],
		queryFn: async () =>
			(await getOfflineDriveParticipateCandidates({
				positionId,
				driveId,
				filters,
			})) as GetOfflineDriveCandidatesResponse,
		staleTime: 0,
		refetchOnMount: true,
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetEligibleDrives = ({
	driveId,
	onComplete,
	handleError,
}: {
	driveId?: string;
	onComplete?: (data: GetEligibleDrivesResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getEligibleDrives', driveId],
		queryFn: async () => (await getEligibleDrives(driveId)) as GetEligibleDrivesResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
export const useGetOfflineDriveRooms = ({
	onComplete,
	handleError,
}: {
	onComplete?: (data: GetOfflineDriveRoomsResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getOfflineDriveRooms'],
		queryFn: async () => (await getOfflineDriveRooms()) as GetOfflineDriveRoomsResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetOfflineDrivePositions = ({
	driveId,
	page,
	limit,
	filters,
	onComplete,
	handleError,
	positionId,
}: {
	driveId: string;
	page: number;
	limit?: number;
	filters?: UserFiltersForApis;
	onComplete?: (data: GetOfflineDrivePositionsResponse) => void;
	handleError?: () => void;
	positionId?: string;
}) => {
	return useQuery({
		queryKey: ['getOfflineDrivePositions', driveId, page, limit, filters, positionId],
		queryFn: async () =>
			(await getOfflineDrivePositions({
				driveId,
				page,
				limit,
				filters,
				positionId,
			})) as GetOfflineDrivePositionsResponse,
		staleTime: 0,
		refetchOnMount: true,
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetDriveApplicationsForUser = ({
	driveId,
	onComplete,
	handleError,
}: {
	driveId: string;
	onComplete?: (data: GetDriveApplicationsForUserResponse) => void;
	handleError?: () => void;
}) => {
	return useQuery({
		queryKey: ['getDriveApplicationsForUser', driveId],
		queryFn: async () =>
			(await getDriveApplicationsForUser({
				driveId,
			})) as GetDriveApplicationsForUserResponse,
		staleTime: 0,
		refetchInterval: DRIVE_API_REFRESH_INTERVAL,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
